import React, { FC } from 'react'
import {
  Typography,
  Box,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Fade,
  keyframes,
} from '@mui/material'
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { useTheme } from '@mui/material/styles'
import {
  Yakureki,
  YakurekiDetail,
  isInProgressStatus,
} from '@/shared/types/yakurekiCore'

// 控えめなAIパルス効果
const aiGentlePulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.08);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
`
import { AudioUploadButton } from '@/apps/legacy/features/yakureki/components/AudioUploadButton'
import GuidanceSupport from '@/apps/legacy/components/molecules/GuidanceSupport'
import GuidanceErrorDisplay from '@/apps/legacy/components/molecules/GuidanceErrorDisplay'
import {
  useGuidanceQuery,
  useSaveGuidanceScriptsMutation,
} from '@/apps/legacy/features/guidance/hooks/queries/useGuidanceQuery'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import PrescriptionInfo from '@/components/molecules/PrescriptionInfo'
import { useToast } from '@/shared/contexts/ToastContext'
import { MOBILE_HEADER_CONFIG } from '@/shared/constants/responsiveBreakpoints'
import { Copyright } from '@/apps/legacy/components/molecules/Copyright'
import { RequestIdDisplay } from '@/apps/legacy/components/molecules/RequestIdDisplay'
import { AIPreparingAnimation } from '@/apps/legacy/components/atoms/AIPreparingAnimation'
import { CollapsibleCard } from '@/apps/legacy/components/molecules/CollapsibleCard'
import { CheckpointCardContainer } from '@/apps/legacy/features/guidance/components/CheckpointCardContainer'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { SPACING } from '@/shared/styles/spacing'
import { NSIPSMemoPanel } from '@/apps/legacy/features/yakureki/components/NSIPSMemoPanel'

export interface PreRecordingScreenContentProps {
  patientDetail: YakurekiDetail
  patientList: Yakureki[]
  renderEditableModal: () => React.ReactNode
  pharmacistId?: string
  isMemoOpen?: boolean
}

/**
 * NSIPS 録音前画面の本体 JSX。
 *
 * 本 component 自体は mode-agnostic (legacy/streaming 共通)。mode 固有の拡張
 * (録音中 transcript 即時表示等) は caller (legacy/streaming wrapper) 側で
 * 差し込む想定.
 */
export const PreRecordingScreenContent: FC<PreRecordingScreenContentProps> = ({
  patientDetail,
  patientList,
  renderEditableModal,
  pharmacistId,
  isMemoOpen = false,
}) => {
  const theme = useTheme()
  const toast = useToast()
  const saveScriptsMutation = useSaveGuidanceScriptsMutation()

  const currentYakureki = patientList.find(
    (yakureki) => yakureki.id === patientDetail.id
  )

  const currentInstitution =
    patientDetail.current_prescription_data?.institution_name
  const previousInstitution =
    patientDetail.previous_prescription_data?.institution_name

  // 服薬指導支援データの取得（自動チェック有効）
  const {
    data: guidanceData,
    isLoading: guidanceLoading,
    error: guidanceError,
    generateGuidance,
  } = useGuidanceQuery(patientDetail.id, { autoCheck: true })

  // フレーズ保存ハンドラー
  const handleSaveScripts = async (
    scriptSelections: Record<string, boolean>,
    scriptOrder: string[]
  ) => {
    try {
      await saveScriptsMutation.mutateAsync({
        yakurekiId: patientDetail.id,
        scriptSelections,
        scriptOrder,
      })
      toast.showToast('選択フレーズを保存しました', 'success')
    } catch {
      toast.showToast('選択フレーズの保存に失敗しました', 'error')
      throw new Error('保存に失敗しました')
    }
  }

  return (
    <>
      {currentYakureki && isInProgressStatus(currentYakureki.status) ? (
        <>
          <Fade in={true}>
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                background:
                  'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1299,
                padding: 3,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 6,
                  textAlign: 'center',
                  maxWidth: 450,
                  width: '100%',
                }}
              >
                <Stack spacing={4} alignItems="center">
                  {/* AIテーマの控えめなアニメーション */}
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: 'rgba(158, 158, 158, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: `${aiGentlePulse} 3s ease-in-out infinite`,
                      }}
                    >
                      <SmartToyIcon
                        sx={{
                          fontSize: 40,
                          color: colorTokens.text.secondary,
                        }}
                      />
                    </Box>
                    {/* 外周のプログレスリング */}
                    <CircularProgress
                      size={95}
                      thickness={1.5}
                      sx={{
                        position: 'absolute',
                        color: colorTokens.grey[500],
                        opacity: 0.4,
                      }}
                    />
                  </Box>

                  {/* タイトルとサブテキスト */}
                  <Stack spacing={2} textAlign="center">
                    <Typography
                      variant="h5"
                      sx={{
                        color: colorTokens.text.primary,
                      }}
                    >
                      SOAP生成中
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: colorTokens.text.secondary,
                        lineHeight: 1.6,
                      }}
                    >
                      AIが音声データを解析し、
                      <br />
                      SOAPノートを生成しています
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colorTokens.text.disabled,
                        fontStyle: 'italic',
                      }}
                    >
                      しばらくお待ちください
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Box>
          </Fade>
          {renderEditableModal()}
        </>
      ) : (
        <>
          {localStorageWrapper.getItem('IsAudioUpload') && (
            <AudioUploadButton pharmacistId={pharmacistId} />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING.sectionGap,
              minHeight: '100vh',
              backgroundColor: theme.palette.secondary.light,
              p: SPACING.mainContent,
              pt: 11,
              [theme.breakpoints.down('sm')]: {
                pt: `${MOBILE_HEADER_CONFIG.contentPaddingTop}px !important`,
              },
            }}
          >
            {/* 処方情報 + 主要確認事項（メモ表示時は左右分割、スマホではメモが上） */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMemoOpen ? 'row' : 'column',
                alignItems: isMemoOpen ? 'stretch' : 'initial',
                gap: SPACING.subSectionGap,
                [theme.breakpoints.down('sm')]: {
                  flexDirection: 'column',
                },
              }}
            >
              {/* 左側: 処方情報 + 主要確認事項（スマホでメモ表示時は下に回る） */}
              <Box
                sx={{
                  flex: isMemoOpen ? '1 1 0%' : 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: SPACING.subSectionGap,
                  [theme.breakpoints.down('sm')]: {
                    order: isMemoOpen ? 2 : 0,
                  },
                }}
              >
                {/* 処方情報エリア - 折りたたみ可能 */}
                <CollapsibleCard title="処方情報" defaultExpanded={true}>
                  <Box
                    sx={{
                      maxHeight: '40vh',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: colorTokens.grey[100],
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: colorTokens.grey[300],
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: colorTokens.grey[400],
                        },
                      },
                    }}
                  >
                    <PrescriptionInfo
                      currentPrescription={
                        patientDetail?.current_prescription_data
                      }
                      previousPrescription={
                        patientDetail?.previous_prescription_data || undefined
                      }
                      currentInstitution={currentInstitution}
                      previousInstitution={previousInstitution}
                    />
                  </Box>
                </CollapsibleCard>

                {/* 主要確認事項カード */}
                <CollapsibleCard title="主要確認事項" defaultExpanded={true}>
                  <CheckpointCardContainer requestId={patientDetail.id} />
                </CollapsibleCard>
              </Box>

              {/* 右側: メモパネル（スマホでは最上部に表示） */}
              {isMemoOpen && (
                <Box
                  sx={{
                    flex: '0 0 38%',
                    display: 'flex',
                    flexDirection: 'column',
                    [theme.breakpoints.down('sm')]: {
                      flex: 'none',
                      order: 1,
                    },
                  }}
                >
                  <NSIPSMemoPanel
                    yakurekiId={patientDetail.id}
                    patientName={patientDetail.label}
                    memoItems={patientDetail.memo_items ?? []}
                  />
                </Box>
              )}
            </Box>

            {/* 服薬指導支援エリア（常にフル幅） */}
            {guidanceLoading ? (
              <Box
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Stack direction="column" spacing={2} alignItems="center">
                  <AIPreparingAnimation />
                  <Typography variant="body2" color="text.secondary">
                    服薬指導を準備中...
                  </Typography>
                </Stack>
              </Box>
            ) : guidanceData ? (
              <CollapsibleCard
                title="服薬指導支援"
                defaultExpanded={true}
                noPadding={true}
              >
                <GuidanceSupport
                  guidanceData={guidanceData}
                  onSaveScripts={handleSaveScripts}
                />
              </CollapsibleCard>
            ) : guidanceError ? (
              <Box
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                }}
              >
                <GuidanceErrorDisplay
                  error={guidanceError?.message ?? ''}
                  onRetry={generateGuidance}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  backgroundColor: colorTokens.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${colorTokens.border.light}`,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={generateGuidance}
                  sx={{
                    color: colorTokens.text.secondary,
                    borderColor: colorTokens.text.secondary,
                    borderWidth: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderColor: colorTokens.text.secondary,
                      borderWidth: '2px',
                    },
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 20 }} />
                  指導を準備
                </Button>
              </Box>
            )}

            {/* Request ID and Copyright */}
            <Box sx={{ mt: 2 }}>
              {patientDetail?.id && (
                <RequestIdDisplay
                  requestId={patientDetail.id}
                  onCopy={async () => {
                    await navigator.clipboard.writeText(patientDetail.id)
                    toast.showToast(
                      'リクエストIDをコピーしました',
                      'info',
                      2000
                    )
                  }}
                />
              )}
              <Copyright />
            </Box>
          </Box>
          {renderEditableModal()}
        </>
      )}
    </>
  )
}
